import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getName } from '@pletly/shared';
import { useAuthHeaders } from '@pletly/feature-auth';
import {
	Contact,
	ContactsSelect,
	LoadingOverlay,
	Modal,
	ModalActions,
	ModalProps,
	Text,
} from '@pletly/ui';
import { TeamMemberDataType } from '../../../components/TeamMembersTable';
import { OrganisationMemberDataType } from '../../../../../view/components/OrganisationMembersTable';
interface Props extends ModalProps {
	teamMembers: TeamMemberDataType[];
	organisationMembers: OrganisationMemberDataType[];
	onSubmit: (selectedMemberIds: string[]) => unknown;
	loading?: boolean;
}

export const AddMembersToTeamModal = ({
	teamMembers,
	organisationMembers,
	onSubmit,
	loading = false,
	...modalProps
}: Props) => {
	const { t } = useTranslation();
	const { getHeaders } = useAuthHeaders();

	const [selectedIds, setSelectedIds] = useState<string[]>([]);

	const currentTeamMembersIds = teamMembers.map(({ memberId }) => memberId) || [];

	const organisationMembersToSelect = organisationMembers.filter(
		({ memberId }) => !currentTeamMembersIds.includes(memberId),
	);

	const contacts: Contact[] = organisationMembersToSelect.map(
		({ memberId, photoUrl, ...member }) => ({
			id: memberId,
			name: getName(member),
			photoSource: photoUrl ? { uri: photoUrl, headers: getHeaders(photoUrl) } : undefined,
		}),
	);

	const handleSubmitPress = useCallback(() => {
		onSubmit(selectedIds);
	}, [onSubmit, selectedIds]);

	return (
		<Modal
			{...modalProps}
			actions={
				<ModalActions
					primaryAction={{
						label: t('assign'),
						onPress: handleSubmitPress,
					}}
					secondaryAction={{
						label: t('common.cancel'),
						onPress: modalProps.onClose,
					}}
				/>
			}
		>
			<LoadingOverlay show={loading} />
			{contacts.length < 1 ? (
				<Text>{t('no-members-to-add')}</Text>
			) : (
				<ContactsSelect
					mode="multi"
					contacts={contacts || []}
					selectedIds={selectedIds}
					setSelectedIds={setSelectedIds}
				/>
			)}
		</Modal>
	);
};
